import React, { useEffect, useRef, useState } from 'react';
import { Screen, AppState } from '../App';
import SignupWizard from './SignupWizard';
import LaptopPreview from '../components/LaptopPreview';

// The immersive first-visit entrance. Visual layer only — every path leads to the
// same SignupWizard → authApi.signup flow used everywhere else.
//
// Flow: branded loading (until the door image is ready) → opening (headline +
// pricing assurance + secondary links) → a continuous camera push through the
// four images → the teacher's desk, where a real sample sits on the laptop with
// one trial button and one subscribe link. Choosing the trial zooms into the
// laptop and hands off to the full-screen signup.
//
// Safety rails: the door image is preloaded before anything shows (no dark flash);
// a load failure falls back to the traditional homepage; prefers-reduced-motion
// and small screens skip straight to the accessible full-screen signup; Skip is
// available throughout.

interface Props {
  navigate: (s: Screen, state?: AppState) => void;
  refreshUser: () => Promise<any>;
  appState?: AppState;
}

const NAMES = ['01-closed-door', '02-open-door', '03-classroom', '04-teacher-desk'];
const SRCS = NAMES.map(n => `/immersive-classroom/${n}.webp`);
const SEEN_KEY = 'dms_entrance_seen';
const PRICE_LINE = 'No credit card required. Continue afterward for $29/month or $299/year.';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type Phase = 'loading' | 'opening' | 'transition' | 'desk';

export default function ClassroomEntrance({ navigate, refreshUser, appState }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [loadedAll, setLoadedAll] = useState(false);
  const [exiting, setExiting] = useState(false);            // opening UI fades out on Enter
  const [deskStage, setDeskStage] = useState<'preview' | 'signup'>('preview');
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  const [mobileFormUp, setMobileFormUp] = useState(false);
  const [waiting, setWaiting] = useState(false); // Enter pressed before preload finished
  const timers = useRef<number[]>([]);
  const reduced = useRef(prefersReducedMotion());

  const markSeen = () => { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ } };
  const clearTimers = () => { timers.current.forEach(t => window.clearTimeout(t)); timers.current = []; };

  // Preload the door image first; reveal the entrance only once it's ready (no
  // dark flash). Then preload the remaining three. Any failure → homepage.
  useEffect(() => {
    let alive = true;
    const first = new Image();
    first.onload = () => {
      if (!alive) return;
      setPhase('opening');
      let loaded = 1;
      SRCS.slice(1).forEach(src => {
        const img = new Image();
        img.onload = () => { if (!alive) return; loaded += 1; if (loaded === SRCS.length) setLoadedAll(true); };
        img.onerror = () => { if (alive) navigate('landing'); };
        img.src = src;
      });
    };
    first.onerror = () => { if (alive) navigate('landing'); };
    first.src = SRCS[0];
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  useEffect(() => clearTimers, []);

  const beginJourney = () => {
    setPhase('transition');
    // A single coordinated CSS timeline (see the .entrance-layer l0–l3 keyframes)
    // drives all four layers at once: overlapping crossfades + continuous forward
    // scaling over ~4.6s, plus a shared push on the scene container. We only
    // schedule the laptop reveal — no per-frame class toggling.
    timers.current.push(window.setTimeout(() => setPhase('desk'), 3800)); // desk settled; laptop fades in
  };

  const enter = () => {
    markSeen();
    if (reduced.current) { setPhase('desk'); return; }
    if (!loadedAll) { setWaiting(true); return; } // wait for preload (button shows "Opening…")
    setExiting(true); // fade the opening UI out fast, then move
    timers.current.push(window.setTimeout(beginJourney, 260));
  };

  // If Enter was pressed before preload finished, continue once ready.
  useEffect(() => {
    if (loadedAll && waiting && phase === 'opening') {
      setWaiting(false);
      setExiting(true);
      timers.current.push(window.setTimeout(beginJourney, 260));
    }
    // eslint-disable-next-line
  }, [loadedAll]);

  const skip = () => { clearTimers(); markSeen(); navigate('signup'); };
  const subscribe = () => { markSeen(); navigate('signup', { intent: 'subscribe' }); };
  const startTrial = () => {
    markSeen();
    // Desktop: zoom the laptop screen up into the signup. Mobile: crossfade the
    // full-screen form up over the desk (a form can't live in the tiny laptop
    // image, and iOS zoom-on-input would break in-laptop positioning).
    if (reduced.current) { navigate('signup'); return; }
    if (wide) setDeskStage('signup'); else setMobileFormUp(true);
  };

  // Brand shows only on the opening screen; the instant Enter is pressed it fades
  // out (0.3s) and never returns — no logo/title floating over the door mid-move.
  const brandVisible = !exiting && phase === 'opening';
  const playing = phase === 'transition' || phase === 'desk';

  // ---- Branded loading state (no dark flash before the door is ready) ----
  if (phase === 'loading') {
    return (
      <div className="entrance-loadscreen" aria-label="Loading" aria-live="polite">
        <img src="/logo.png" alt="" className="entrance-load-logo" />
        <span className="entrance-load-name">Decode My Student</span>
        <div className="spinner entrance-load-spin" />
      </div>
    );
  }

  // ---- Signup content, shared by the full-screen and mobile-crossfade forms ----
  const signupInner = (
    <div style={{ width: '100%', maxWidth: 460 }}>
      <SignupWizard navigate={navigate} refreshUser={refreshUser} appState={appState} onExit={() => navigate('landing')} />
      <p style={{ textAlign: 'center', marginTop: 16 }}>
        <button onClick={subscribe} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>Skip the trial and subscribe</button>
      </p>
    </div>
  );

  // Reduced-motion desktop skips the scene entirely and goes straight to the form.
  if (phase === 'desk' && reduced.current) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
        {signupInner}
      </div>
    );
  }

  const deskDesktop = phase === 'desk' && wide;
  const deskMobilePreview = phase === 'desk' && !wide && !mobileFormUp; // demo + CTAs on the desk
  const deskMobileForm = phase === 'desk' && !wide && mobileFormUp;     // full-screen form fades up over the desk
  const signingUp = deskDesktop && deskStage === 'signup';

  return (
    <div className={`entrance-stage${!wide ? ' entrance-stage--mobile' : ''}${signingUp ? ' is-dimmed' : ''}`} aria-label="Classroom entrance">
      <div className={`entrance-layers${playing ? ' is-playing' : ''}`}>
        {SRCS.map((src, i) => (
          <div key={src} aria-hidden="true" className={`entrance-layer l${i}`} style={{ backgroundImage: `url("${src}")` }} />
        ))}
      </div>
      <div className="entrance-scrim" aria-hidden="true" />

      {/* Brand lockup on the door (fades out the instant the journey begins) */}
      <div className="entrance-brand" style={{ opacity: brandVisible ? 1 : 0 }} aria-hidden="true">
        <img src="/logo.png" alt="" className="entrance-brand-logo" />
        <span className="entrance-brand-name">Decode My Student</span>
      </div>

      {/* Opening overlay */}
      {phase === 'opening' && (
        <div className="entrance-overlay entrance-overlay--opening" style={{ opacity: exiting ? 0 : 1, transition: 'opacity 0.25s ease' }}>
          <div className="entrance-card animate-in">
            <h1 style={{ color: '#fff', fontSize: 34, lineHeight: 1.15, marginBottom: 10 }}>Your classroom is waiting.</h1>
            <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: 17, marginBottom: 20 }}>Let's make it a little easier to understand.</p>
            <button className="btn-primary" style={{ width: '100%', maxWidth: 340 }} onClick={enter}>
              {waiting ? 'Opening…' : 'Enter the classroom'}
            </button>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.5, marginTop: 12, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              Free for 7 days. No credit card required. Continue afterward for $29/month or $299/year.
            </p>
            <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginTop: 18 }}>
              <button onClick={() => navigate('landing')} className="entrance-textbtn">View features and pricing</button>
              <button onClick={() => navigate('login')} className="entrance-textbtn">Log in</button>
            </div>
          </div>
        </div>
      )}

      {/* Skip stays available through the animation */}
      {phase === 'transition' && <button className="entrance-skip" onClick={skip}>Skip →</button>}

      {/* Desk, mobile: same demo + CTAs as desktop, shown as a readable card over
          the desk (a form can't live in the tiny laptop image on a phone). */}
      {deskMobilePreview && (
        <div className="entrance-desk entrance-desk--mobile animate-in">
          <button className="entrance-skip" onClick={() => navigate('landing')}>See the homepage →</button>
          <div className="mobile-demo-card">
            <LaptopPreview />
            <button className="btn-primary" style={{ marginTop: 12, padding: '12px 18px' }} onClick={startTrial}>Start your free 7-day trial</button>
            <p style={{ fontSize: 11.5, color: 'var(--text-secondary)', textAlign: 'center', margin: '8px 2px 0', lineHeight: 1.45 }}>{PRICE_LINE}</p>
            <button className="laptop-sublink" onClick={subscribe}>Skip the trial and subscribe</button>
          </div>
        </div>
      )}

      {/* Mobile: the full-screen form crossfades up over the desk (no hard cut). */}
      {deskMobileForm && (
        <div className="entrance-mobile-form animate-in">{signupInner}</div>
      )}

      {/* Desk, desktop: real sample on the laptop; one trial button + one subscribe link */}
      {deskDesktop && (
        <div className="entrance-desk animate-in">
          <div className={`desk-laptop${signingUp ? ' is-expanding' : ''}`}>
            <div className="desk-laptop-screen">
              {deskStage === 'preview' ? (
                <>
                  <LaptopPreview />
                  <button className="btn-primary" style={{ marginTop: 10, padding: '10px 18px', fontSize: 14 }} onClick={startTrial}>Start your free 7-day trial</button>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', margin: '7px 2px 0', lineHeight: 1.4 }}>{PRICE_LINE}</p>
                  <button className="laptop-sublink" onClick={subscribe}>Skip the trial and subscribe</button>
                </>
              ) : (
                <div className="desk-signup animate-in">
                  <SignupWizard navigate={navigate} refreshUser={refreshUser} appState={appState} onExit={() => navigate('landing')} />
                  <p style={{ textAlign: 'center', marginTop: 14 }}>
                    <button onClick={subscribe} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>Skip the trial and subscribe</button>
                  </p>
                </div>
              )}
            </div>
          </div>
          {deskStage === 'preview' && <button className="entrance-skip" onClick={() => navigate('landing')}>See the homepage →</button>}
        </div>
      )}
    </div>
  );
}
